import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../../styles/assessment.css";
import QuestionRenderer from "./QuestionRenderer";
import { MathJax, MathJaxContext } from "better-react-mathjax";
import { convertMathJax } from "../../utils/QuestionsUtils";

const shuffleArray = (array) => {
  return [...array].sort(() => Math.random() - 0.5);
};

const Assessment = ({ questionsData, timerDuration = 1 }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { subjectName, chapterNames } = location.state || {};

  const [questions, setQuestions] = useState(shuffleArray(questionsData));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [score, setScore] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(timerDuration * 60); // Full assessment timer
  const [showHint, setShowHint] = useState(false);

  const nextQuestion = useCallback(() => {
    setCurrentIndex((prevIndex) =>
      prevIndex < questions.length - 1 ? prevIndex + 1 : prevIndex
    );
    setShowHint(false);
  }, [questions.length]);

  const prevQuestion = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
      setShowHint(false);
    }
  };

  const exitAssessment = () => {
    if (window.confirm("Are you sure you want to exit the assessment?")) {
      navigate("/assessments");
    }
  };

  const getCorrectHint = (question) => {
    const correctChoice = question?.answerChoices?.find(choice => choice.isCorrect);
    return correctChoice?.hint || null;
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timer);
          handleSubmit(); // Auto-submit on time out
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate, questions, answers]);

  const handleSelect = (option) => {
    setAnswers((prev) => ({
      ...prev,
      [questions[currentIndex].id]: option
    }));
  };

  const handleSubmit = () => {
    let finalScore = 0;

    questions.forEach((question) => {
      const userAnswer = answers[question.id];
      const correctAnswer = question.answerChoices?.find(choice => choice.isCorrect)?.choiceText;
      if (userAnswer && correctAnswer && userAnswer === correctAnswer) {
        finalScore++;
      }
    });

    setScore(finalScore);
    setSubmitted(true);
    navigate("/results", { state: { score: finalScore, answers, questions } });
  };

  return (
    <div className="assessment-container">
      <div className="assessment-header">
        <h1>{subjectName || "Assessment"}</h1>
      </div>

      <div className="main-content">
        <div className="question-section">
          <QuestionRenderer question={questions[currentIndex]} index={currentIndex} />

          {questions[currentIndex]?.answerChoices
            ?.slice()
            .sort((a, b) => a.id - b.id)
            .map((choice) => (
              <label
                key={choice.id}
                className={`option ${answers[questions[currentIndex].id] === choice.choiceText ? "selected" : ""
                  }`}
              >
                <input
                  type="radio"
                  name={questions[currentIndex].id}
                  value={choice.choiceText}
                  onChange={() => handleSelect(choice.choiceText)}
                  disabled={submitted || timeLeft === 0}
                />
                <MathJaxContext>

                  {convertMathJax(choice.choiceText)}
                </MathJaxContext>
              </label>
            ))}


          <div className="assessment-buttons">
            <button className="button button-danger" onClick={exitAssessment}>Exit</button>
            <button className="button button-primary" onClick={prevQuestion}>Previous</button>
            {currentIndex < questions.length - 1 ? (
              <button className="button button-secondary" onClick={nextQuestion}>Next</button>
            ) : (
              <button className="button button-warning" onClick={handleSubmit}>Submit</button>
            )}
          </div>
        </div>

        <div className="sidebar-section">
          <div className="timer-box">
            <strong>Time Left:</strong>{" "}
            {Math.floor(timeLeft / 60)}m {timeLeft % 60}s
          </div>
          <div className="progress-bar">
            <p>Question {currentIndex + 1} / {questions.length}</p>
            <progress value={currentIndex + 1} max={questions.length}></progress>
          </div>



          {getCorrectHint(questions[currentIndex]) && (
            <button
              className="button button-secondary"
              onClick={() => setShowHint((prev) => !prev)}
            >
              {showHint ? "Hide Hint" : "Show Hint"}
            </button>
          )}

          {showHint && (
            <p className="hint-text">{getCorrectHint(questions[currentIndex])}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Assessment;
